import { getUrlBasedOnPortal } from 'app-constants/spectrum-config';
import { PageNSTranslationFileKeys } from 'translation/hooks';
import { isSMBPortal } from 'utils/applicationHelper';

export interface ItemInterface {
  uiName: string | PageNSTranslationFileKeys<'linksCopy'>;
  href?: string;
  baseUrl?: string;
  label?: PageNSTranslationFileKeys<'linksCopy'>;
  stdName?: string;
  icon?: string;
  name?: string;
}

export interface LinkItemInterface extends ItemInterface {
  uiName: PageNSTranslationFileKeys<'linksCopy'>;
  label?: PageNSTranslationFileKeys<'linksCopy'>;
  href: string;
}

export interface LogoItemInterface extends ItemInterface {
  href: string;
  label?: PageNSTranslationFileKeys<'linksCopy'>;
}

export type LinkGroupType = {
  title?: PageNSTranslationFileKeys<'linksCopy'>;
  links: (LinkItemInterface | ItemInterface | LogoItemInterface)[];
  perColumn?: number;
};

const currentYear = new Date().getFullYear();

export const COPY_RIGHT: ItemInterface = {
  uiName: `© ${currentYear} Charter Communications`,
};

const getYourPrivacyRightLink = (): LinkItemInterface => ({
  uiName: 'footerYourPrivacyRightLabel',
  href: getUrlBasedOnPortal('YOUR_PRIVACY_URL'),
  stdName: 'footer-legal-privacyRights',
});
const getCaliforniaConsumerPrivacyRightLink = (): LinkItemInterface => ({
  uiName: 'footerCaliforniaConsumerPrivacyRightsLabel',
  href: getUrlBasedOnPortal('CALIFORNIA_PRIVACY_RIGHT_URL'),
  stdName: 'footer-legal-californiaPrivacyRights',
});
const getCaliforniaDoNotSellOptOutLink = (): LinkItemInterface => ({
  uiName: 'footerDoNotSellOptOutLabel',
  href: getUrlBasedOnPortal('DO_NOT_SELL_DATA_OPT_OUT_URL'),
  stdName: 'footer-legal-californiaResidentDontSellMyInfo',
});
const getPoliciesLink = (): LinkItemInterface => ({
  uiName: 'footerPoliciesLabel',
  href: getUrlBasedOnPortal('POLICIES_URL'),
  stdName: 'footer-legal-policies',
});
const getCaliforniaLimitedUseOfSensitiveInfoLink = (): LinkItemInterface => ({
  uiName: 'footerCaliforniaLimitedUseOfSensitivePersonalInfoLabel',
  href: getUrlBasedOnPortal('CALIFORNIA_CONSUMER_SENSITIVE_PERSONAL_INFO_URL'),
  stdName: 'footer-legal-california-limited-use-of-sensitive-personal-info',
});
const getOpenSourceLibrariesLink = (): LinkItemInterface => ({
  uiName: 'footerOpenSourceLibrariesLabel',
  href: getUrlBasedOnPortal('OPEN_SOURCE_LIBRARIES'),
  stdName: 'footer-legal-open-source-libraries',
});
const getServiceRateDisclosuresLink = (): LinkItemInterface => ({
  uiName: 'footerServiceRateDisclosures',
  href: getUrlBasedOnPortal('SERVICE_RATE_DISCLOSURES_URL'),
  stdName: 'footer-service-rate-disclosures',
});

const getPortalSupportLink = (): LinkItemInterface => ({
  uiName: 'support',
  href: getUrlBasedOnPortal('SUPPORT_URL'),
  stdName: 'accountSupport',
  label: 'supportAria',
});
const getPortalAccountSummaryLink = (): LinkItemInterface => ({
  uiName: 'account',
  href: getUrlBasedOnPortal('ACCOUNT_SUMMARY_URL'),
  stdName: 'accountSummary',
});

const getPortalChangePasswordUrl = (): string => getUrlBasedOnPortal('CHANGE_PASSWORD_URL');

const getPortalSupportLinkAccount = (): LinkItemInterface => ({
  uiName: 'accountSupport',
  href: getUrlBasedOnPortal('SUPPORT_ACCOUNT'),
  stdName: 'footer-getSupport-accountSupport',
});
const getPortalSupportLinkInternet = (): LinkItemInterface => ({
  uiName: 'internetSupport',
  href: getUrlBasedOnPortal('SUPPORT_INTERNET'),
  stdName: 'footer-getSupport-internet',
});
const getPortalSupportLinkTV = (): LinkItemInterface => ({
  uiName: 'tvSupport',
  href: getUrlBasedOnPortal('SUPPORT_TV'),
  stdName: 'footer-getSupport-tv',
});
const getPortalSupportLinkVoice = (): LinkItemInterface => ({
  uiName: isSMBPortal() ? 'businessPhoneSupport' : 'voiceSupport',
  href: getUrlBasedOnPortal('SUPPORT_VOICE'),
  stdName: 'footer-getSupport-voice',
});
const getPortalSupportLinkMobile = (): LinkItemInterface => ({
  uiName: 'mobileSupport',
  href: getUrlBasedOnPortal('SUPPORT_MOBILE'),
  stdName: 'footer-manageAccount-mobile',
});
const getPortalSupportLinkAccessibility = (): LinkItemInterface => ({
  uiName: 'accessibilitySupport',
  href: getUrlBasedOnPortal('SUPPORT_ACCESSIBILITY'),
  stdName: 'footer-getSupport-accessibility',
});

const getPortalWatchTVLinkLiveTV = (): LinkItemInterface => ({
  uiName: 'liveWatchTv',
  href: getUrlBasedOnPortal('WATCH_TV_LIVE_TV'),
  stdName: 'footer-watchTV-liveTV',
});
const getPortalWatchTVLinkGuide = (): LinkItemInterface => ({
  uiName: 'guideWatchTv',
  href: getUrlBasedOnPortal('WATCH_TV_GUIDE'),
  stdName: 'footer-watchTV-guide',
});
const getPortalWatchTVLinkMyLibrary = (): LinkItemInterface => ({
  uiName: 'libraryWatchTv',
  href: getUrlBasedOnPortal('WATCH_TV_MY_LIBRARY'),
  stdName: 'footer-watchTV-liveTV',
});
const getPortalWatchTVLinkOnDemand = (): LinkItemInterface => ({
  uiName: 'ondemandWatchTv',
  href: getUrlBasedOnPortal('WATCH_TV_ON_DEMAND'),
  stdName: 'footer-watchTV-onDemand',
});
const getPortalWatchTVLinkDVR = (): LinkItemInterface => ({
  uiName: 'dvrWatchTv',
  href: getUrlBasedOnPortal('WATCH_TV_DVR'),
  stdName: 'footer-watchTV-dvr',
});

const getPortalFooterLinkFindStore = (): LinkItemInterface => ({
  uiName: 'location',
  href: getUrlBasedOnPortal('FOOTER_FIND_STORE'),
  stdName: 'footer-contact-findStore',
});
const getPortalFooterLinkWeatherOutage = (): LinkItemInterface => ({
  uiName: 'weather',
  href: getUrlBasedOnPortal('FOOTER_WEATHER_OUTAGE'),
  stdName: 'footer-contact-weatherOutage',
});
const getPortalFooterLinkCustomerCommitment = (): LinkItemInterface => ({
  uiName: 'customerCommitment',
  href: getUrlBasedOnPortal('FOOTER_CUSTOMER_COMMITMENT'),
  stdName: 'footer-customer-commitment',
});
const getPortalFooterLinkContactUs = (): LinkItemInterface => ({
  uiName: 'contact',
  href: getUrlBasedOnPortal('FOOTER_CONTACT_US'),
  stdName: 'footer-contact-contactUs',
});
const getPortalFooterLinkEnergyUsage = (): LinkItemInterface => ({
  uiName: 'energyUsage',
  href: getUrlBasedOnPortal('FOOTER_ENERGY_USAGE'),
  stdName: 'footer-energy-usage',
});

const getPortalSocialMediaLinkX = (): LinkItemInterface => ({
  uiName: 'twitterLabel',
  href: getUrlBasedOnPortal('SOCIAL_MEDIA_X'),
  stdName: 'footer-social-twitter',
  icon: 'x-twitter',
});
const getPortalSocialMediaLinkFacebook = (): LinkItemInterface => ({
  uiName: 'facebookLabel',
  href: getUrlBasedOnPortal('SOCIAL_MEDIA_FACEBOOK'),
  stdName: 'footer-social-facebook',
  icon: 'facebook',
});
const getPortalSocialMediaLinkLinkedin = (): LinkItemInterface => ({
  uiName: 'linkedinLabel',
  href: getUrlBasedOnPortal('SOCIAL_MEDIA_LINKEDIN'),
  stdName: 'footer-social-linkedin',
  icon: 'linkedin',
});
const getPortalSocialMediaLinkInstagram = (): LinkItemInterface => ({
  uiName: 'instagramLabel',
  href: getUrlBasedOnPortal('SOCIAL_MEDIA_INSTAGRAM'),
  stdName: 'footer-social-instagram',
  icon: 'instagram',
});
const getPortalSocialMediaLinkYoutube = (): LinkItemInterface => ({
  uiName: 'youtubeLabel',
  href: getUrlBasedOnPortal('SOCIAL_MEDIA_YOUTUBE'),
  stdName: 'footer-social-youtube',
  icon: 'youtube',
});

export {
  getUrlBasedOnPortal,
  getYourPrivacyRightLink,
  getCaliforniaConsumerPrivacyRightLink,
  getCaliforniaDoNotSellOptOutLink,
  getPoliciesLink,
  getServiceRateDisclosuresLink,
  getCaliforniaLimitedUseOfSensitiveInfoLink,
  getOpenSourceLibrariesLink,
  getPortalSupportLink,
  getPortalChangePasswordUrl,
  getPortalAccountSummaryLink,
  getPortalSupportLinkAccessibility,
  getPortalSupportLinkInternet,
  getPortalSupportLinkTV,
  getPortalSupportLinkVoice,
  getPortalSupportLinkMobile,
  getPortalSupportLinkAccount,
  getPortalWatchTVLinkLiveTV,
  getPortalWatchTVLinkGuide,
  getPortalWatchTVLinkMyLibrary,
  getPortalWatchTVLinkOnDemand,
  getPortalWatchTVLinkDVR,
  getPortalFooterLinkFindStore,
  getPortalFooterLinkWeatherOutage,
  getPortalFooterLinkCustomerCommitment,
  getPortalFooterLinkContactUs,
  getPortalFooterLinkEnergyUsage,
  getPortalSocialMediaLinkX,
  getPortalSocialMediaLinkFacebook,
  getPortalSocialMediaLinkLinkedin,
  getPortalSocialMediaLinkInstagram,
  getPortalSocialMediaLinkYoutube,
};
